import React from 'react';
import PropTypes from 'prop-types';
import ListOfPages from './ListOfPages';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/pagination/ListOfPages',
  component: ListOfPages,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `ListOfPages` component dynamically renders a list of page numbers for pagination. It is designed to handle both small and large datasets by displaying ellipses for compact representation when the number of pages exceeds 15.\n\n',
      // When & how to use it?
      '\nUse the `ListOfPages` component in any paginated layout to provide users with easy navigation between pages. It adapts dynamically to the number of pages and supports clickable page buttons.\n\n',
      // Current Usage
      '\nThe `ListOfPages` component is integrated into the `Pagination` component, enabling users to navigate through paginated content seamlessly. It ensures a user-friendly experience even for datasets with a large number of pages.\n\n',
      // Related Components
      '\n- **[Pagination](http://localhost:6006/?path=/docs/components-pagination-pagination--docs)**: The parent component that integrates `ListOfPages` to manage pagination.\n\n',
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    numberOfPages: {
      control: { type: 'number' },
      description: 'The total number of pages available for pagination.\n\nWhen more than 15 pages are present, the component displays ellipses for compact navigation.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    currentPage: {
      control: { type: 'number' },
      description: 'The currently active page number. This determines which page button is highlighted.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    setCurrentPage: {
      control: { type: 'function' },
      description: 'Callback function triggered when a page button is clicked. Updates the active page dynamically.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
  },
};

const ListOfPagesTemplate = ({ numberOfPages }) => {
  const [currentPage, setCurrentPage] = React.useState(1);

  return (
    <div className="flex justify-center">
      <ListOfPages numberOfPages={numberOfPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
    </div>
  );
};

ListOfPagesTemplate.propTypes = {
  numberOfPages: PropTypes.number.isRequired,
};

const Template = ListOfPagesTemplate.bind({});

export const DefaultListOfPages = Template.bind({});
DefaultListOfPages.args = {
  numberOfPages: 10,
  currentPage: 1,
};
DefaultListOfPages.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used to display a list of page numbers for paginated layouts, with dynamic ellipses for navigation in datasets with more than 15 pages.\n\n',
  // Where is it currently used on the product?
  '\nThe `ListOfPages` component is used as part of the `Pagination` component to provide navigation through paginated content.\n\n',
);
